import { GroupByEnums } from '../../../../components/orders-grid/group-by-enums/index';
import { multipleLabelText, ordersGridContractOrderByParamReverseMap } from '../../../../services/column-orderby-param-service';
import { lineItemSelector } from './selectors';

const createLineItem = ({ fulfillmentPlan, salesOrder, item = {}, salesOrderInfoToGroupBy }) => {
  const { salesOrderId } = salesOrder;
  const { fulfillmentPlanId } = fulfillmentPlan;

  const { fulfillmentPlanItemId } = item;

  return lineItemSelector({
    id: fulfillmentPlanId,
    salesOrderId,
    fulfillmentPlanItemId,
    salesOrderInfoToGroupBy,
  });
};

export const getSalesOrderLookupHash = (salesOrders) =>
  salesOrders.reduce((acc, salesOrder) => {
    if (salesOrder) {
      return {
        ...acc,
        [salesOrder.salesOrderId]: salesOrder,
      };
    }
    return acc;
  }, {});

export const populateLineItems = (salesOrderId = undefined, matchSalesOrder = false, fulfillmentPlan, salesOrderLookupHash, stores) => {
  const { items } = fulfillmentPlan;

  const salesOrder = salesOrderLookupHash[salesOrderId];
  if (!salesOrder) {
    return;
  }
  const itemsFromSalesOrder = salesOrder.items || [];
  const salesOrderItemLookupHash = itemsFromSalesOrder.reduce((acc, item) => {
    return {
      ...acc,
      [item.salesOrderItemId]: true,
    };
  }, {});

  const salesOrderItems = matchSalesOrder ? items.filter((item) => !!salesOrderItemLookupHash[item.salesOrderItemId]) : items;

  // this object is managing the information that we need in all cases to group by
  const salesOrderInfoToGroupBy = {
    Store: stores.find((store) => salesOrder?.store?.storeGuid === store.storeGuid)?.name || null,
    Country: fulfillmentPlan?.shipTo?.countryCode || fulfillmentPlan?.shipTos?.[0]?.countryCode || null,
    LineItemCount: salesOrderItems.length.toString(),
    Quantity: salesOrderItems.reduce((acc, item) => item.quantity + acc, 0).toString(),
    RequestedService: salesOrder.requestedService || null,
  };

  if (salesOrderItems.length === 0) {
    salesOrderInfoToGroupBy.ItemSku = null;
    salesOrderInfoToGroupBy.ItemName = null;
    return createLineItem({
      salesOrder,
      fulfillmentPlan,
      salesOrderInfoToGroupBy,
    });
  }
  // if the length is 1, we can just return the line item because if we leave the code to reach the next block, with one item, we will have an array as result of the map
  if (salesOrderItems.length === 1) {
    const [item] = salesOrderItems;
    salesOrderInfoToGroupBy.ItemSku = item.sku;
    salesOrderInfoToGroupBy.ItemName = item.name;
    return createLineItem({
      fulfillmentPlan,
      item,
      salesOrder,
      salesOrderInfoToGroupBy,
    });
  }

  salesOrderInfoToGroupBy.ItemSku = multipleLabelText;
  salesOrderInfoToGroupBy.ItemName = multipleLabelText;
  return salesOrderItems.map((item) =>
    createLineItem({
      fulfillmentPlan,
      item,
      salesOrder,
      salesOrderInfoToGroupBy,
    }),
  );
};

export const parseFulfillmentPlantToLineItems = (fulfillmentPlans, salesOrderLookupHash, stores) =>
  fulfillmentPlans.reduce((lineItems, fulfillmentPlan) => {
    // oxlint-disable-next-line no-param-reassign
    fulfillmentPlan.salesOrdersFulfilledIds = [...new Set(fulfillmentPlan.salesOrdersFulfilledIds)].sort();

    if (fulfillmentPlan.salesOrdersFulfilledIds.length === 1) {
      const [salesOrderId] = fulfillmentPlan.salesOrdersFulfilledIds;
      lineItems.push(populateLineItems(salesOrderId, false, fulfillmentPlan, salesOrderLookupHash, stores));
    } else {
      const lineItemGroup = [];

      if (fulfillmentPlan.salesOrdersFulfilledIds.length) {
        fulfillmentPlan.salesOrdersFulfilledIds.forEach((salesOrderId) => {
          lineItemGroup.push([populateLineItems(salesOrderId, true, fulfillmentPlan, salesOrderLookupHash, stores)]);
        });

        lineItems.push(lineItemGroup);
      }
    }

    return lineItems.filter((lineItem) => !!lineItem);
  }, []);

const isValueInsideGroup = (current, { groupByColumn, groupByValue }) => {
  return current.length > 1
    ? current[0].salesOrderInfoToGroupBy?.[groupByColumn] === groupByValue
    : current.salesOrderInfoToGroupBy?.[groupByColumn] === groupByValue;
};

const getGroupByDetailIndex = (data, groupDetails) => {
  return groupDetails.findIndex(({ groupByDetails }) =>
    groupByDetails.every(({ groupByColumn, groupByValue }) => isValueInsideGroup(data, { groupByColumn, groupByValue })),
  );
};

const hasGroupCreated = (arr, index) => index !== -1 && !!arr[index];

export const getGroupByInfoArr = (groupDetails = {}) => {
  return [
    {
      groupById: groupDetails.groupById, // this is valueless, it was autogenerated by GuidService when we saved it in the store
      totalRecordCount: groupDetails.count,
      groupByDetails: groupDetails.groupByDetails?.map(({ groupByColumn, groupByValue }) => {
        // This should pull the column from the active enums that matches for internationalization
        const groupByEnumKey = ordersGridContractOrderByParamReverseMap[groupByColumn];
        return {
          groupByName: { ...GroupByEnums[groupByEnumKey].messageDescriptor },
          groupByValue,
          groupByColumn,
        };
      }),
    },
  ];
};

export const getGridData = (data, groupByColumnDetailsAndCounts) => {
  const gridDataUnfiltered = data.reduce((acc, current) => {
    const groupIndex = getGroupByDetailIndex(current, groupByColumnDetailsAndCounts);

    if (groupIndex === -1) {
      return acc;
    }

    if (!hasGroupCreated(acc, groupIndex)) {
      const groupDetails = groupByColumnDetailsAndCounts[groupIndex];
      // oxlint-disable-next-line no-param-reassign
      acc[groupIndex] = getGroupByInfoArr(groupDetails);
    }

    const selectedGroup = acc[groupIndex];
    selectedGroup.push(current);

    return acc;
  }, []);

  // with this step, we clean the array when the indexes of the groups are not consecutive, most of them for pages > 1
  return gridDataUnfiltered.filter((group) => !!group);
};

export const buildGridDataIndexMap = (data) => {
  const dataIsFormattedForGroupBy = data.some((datum) => Array.isArray(datum) && datum[0].groupById);
  const mapSource = dataIsFormattedForGroupBy
    ? // Remove the groupBy header and flatten the data
      data.reduce((groupByData, datum) => {
        const withOutGroupByHeader = datum.slice(1);
        return groupByData.concat(withOutGroupByHeader);
      }, [])
    : data;
  // SCHH: data is large. Make a map for constant lookup for the sort below
  const indexMap = new Map(
    mapSource.map((datum, index) => {
      return Array.isArray(datum) ? [datum[0].id, index] : [datum.id, index];
    }),
  );
  return indexMap;
};

export const sortSelectedOrderIdsByGridOrder = (selectedOrderIds, indexMap) => {
  const sortedSelectedOrderIds = [...selectedOrderIds].sort((a, b) => {
    const aIndex = indexMap.get(a);
    const bIndex = indexMap.get(b);
    return aIndex - bIndex;
  });
  return sortedSelectedOrderIds;
};
